import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { toast } from 'sonner';
import { api, ApiError } from '@/lib/api';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Spinner } from '@/components/ui/spinner';
import { PageHeader } from '@/layouts/AppLayout';

interface Client {
  id: string;
  name: string;
  type: 'travel_agent' | 'b2c' | 'corporate';
  contactPerson: string | null;
  contactEmail: string | null;
  contactPhone: string | null;
  city: string | null;
  country: string;
  status: 'active' | 'inactive';
  creditDays: number;
  createdAt: string;
}

const TYPE_LABELS: Record<Client['type'], string> = {
  travel_agent: 'Travel Agent',
  b2c: 'B2C',
  corporate: 'Corporate',
};

export function ClientsListPage() {
  const qc = useQueryClient();

  const { data: clients, isLoading } = useQuery({
    queryKey: ['clients'],
    queryFn: () => api.get<Client[]>('/clients'),
  });

  const deleteMutation = useMutation({
    mutationFn: (id: string) => api.delete(`/clients/${id}`),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['clients'] });
      toast.success('Client deleted');
    },
    onError: (err) => {
      const msg = err instanceof ApiError ? err.message : 'Failed to delete client';
      toast.error(msg);
    },
  });

  const handleDelete = (client: Client) => {
    if (!confirm(`Delete "${client.name}"? This cannot be undone.`)) return;
    deleteMutation.mutate(client.id);
  };

  return (
    <div>
      <PageHeader
        title="Clients"
        description="Travel agents, corporates, and B2C guests"
        action={
          <Button asChild>
            <Link to="/clients/new">Add client</Link>
          </Button>
        }
      />
      <div className="p-6">
        {isLoading ? (
          <div className="flex justify-center py-16"><Spinner className="h-6 w-6" /></div>
        ) : !clients?.length ? (
          <div className="rounded-lg border border-dashed border-neutral-300 py-16 text-center text-sm text-neutral-500">
            No clients yet.{' '}
            <Link to="/clients/new" className="text-tangerine-600 hover:underline">
              Add the first one.
            </Link>
          </div>
        ) : (
          <div className="rounded-lg border border-neutral-200 bg-white overflow-hidden">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-neutral-200 bg-neutral-50">
                  <th className="px-4 py-3 text-left font-medium text-neutral-500">Name</th>
                  <th className="px-4 py-3 text-left font-medium text-neutral-500">Type</th>
                  <th className="px-4 py-3 text-left font-medium text-neutral-500">Contact</th>
                  <th className="px-4 py-3 text-left font-medium text-neutral-500">City</th>
                  <th className="px-4 py-3 text-left font-medium text-neutral-500">Status</th>
                  <th className="px-4 py-3 text-right font-medium text-neutral-500">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-neutral-100">
                {clients.map((client) => (
                  <tr key={client.id} className="hover:bg-neutral-50 transition-colors">
                    <td className="px-4 py-3">
                      <Link
                        to={`/clients/${client.id}`}
                        className="font-medium text-neutral-900 hover:text-tangerine-600"
                      >
                        {client.name}
                      </Link>
                      {client.creditDays > 0 && (
                        <span className="ml-2 text-xs text-neutral-400">{client.creditDays}d credit</span>
                      )}
                    </td>
                    <td className="px-4 py-3">
                      <Badge variant={client.type}>{TYPE_LABELS[client.type]}</Badge>
                    </td>
                    <td className="px-4 py-3 text-neutral-600">
                      <div>{client.contactPerson ?? '—'}</div>
                      {client.contactEmail && (
                        <div className="text-xs text-neutral-400">{client.contactEmail}</div>
                      )}
                    </td>
                    <td className="px-4 py-3 text-neutral-600">{client.city ?? '—'}</td>
                    <td className="px-4 py-3">
                      <Badge variant={client.status}>{client.status}</Badge>
                    </td>
                    <td className="px-4 py-3 text-right space-x-2">
                      <Button variant="ghost" size="sm" asChild>
                        <Link to={`/clients/${client.id}/edit`}>Edit</Link>
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        className="text-danger-700 hover:text-danger-700 hover:bg-danger-50"
                        onClick={() => handleDelete(client)}
                        disabled={deleteMutation.isPending}
                      >
                        Delete
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
